import React, { useState } from 'react';
import classnames from 'classnames';

import ReactJson from 'react-json-view';
import { DataGrid } from '@codametrix/ui-components';
import { objectUtils } from '@codametrix/ui-common';
import './styles/index.scss';
export type DMNTableProps = React.HTMLAttributes<HTMLDivElement> & {
  step: CMxAPI.BPMStep;
};

const INPUT_COL_DEFS: any[] = [
  {
    key: 'key',
    displayName: 'Key',
    isSortable: false,
    defaultSort: 'none'
  },
  {
    key: 'value',
    displayName: 'Value',
    isSortable: false,
    defaultSort: 'none',
    transform: (data: any) => {
      const isJson = objectUtils.isStringifiedJSON(data.value);
      let content = data.value;

      if (isJson) {
        content = (
          <ReactJson
            name={false}
            src={objectUtils.getValueForString(data.value)}
            displayObjectSize={false}
            collapsed={1}
            displayDataTypes={false}
            enableClipboard={false}
            theme={'rjv-default'}
          />
        );
      }
      return content;
    }
  }
];

const OUTPUT_COL_DEFS: any[] = [
  {
    key: 'mappedKey',
    displayName: 'Key',
    isSortable: false,
    defaultSort: 'none'
  },
  {
    key: 'mappedValue',
    displayName: 'Value',
    isSortable: false,
    defaultSort: 'none',
    transform: (data: any) => {
      const isJson = objectUtils.isStringifiedJSON(data.mappedValue);
      let content = data.mappedValue;

      if (isJson) {
        content = (
          <ReactJson
            name={false}
            src={objectUtils.getValueForString(data.mappedValue)}
            displayObjectSize={false}
            collapsed={1}
            displayDataTypes={false}
            enableClipboard={false}
            theme={'rjv-default'}
          />
        );
      }
      return content;
    }
  }
];

const groupInputs = (inputs: CMxAPI.DecisionInput[]) => {
  return objectUtils.groupBy(inputs, 'source');
};

const displayMatchedRule = (rule?: CMxAPI.DecisionRule) => {
  if (!rule) {
    return false;
  }
  return rule.decisionRuleConditions.map(condition => {
    return (
      <code className="rule-condition" key={condition.conditionId}>
        {condition.condition}
      </code>
    );
  });
};

const displayInputs = (inputs: CMxAPI.DecisionInput[]) => {
  const groups = groupInputs(inputs);
  return Object.entries(groups).map(([key, values]: [string, CMxAPI.DecisionInput[]]) => {
    const table = (
      <DataGrid
        idField="key"
        columnDefs={INPUT_COL_DEFS}
        items={values}
        selectable={false}
      />
    );
    return (
      <div key={key} className="input-table">
        <h5 className="io-heading">{key}</h5> {table}
      </div>
    );
  });
};

const displayOutputs = (outputs: CMxAPI.DecisionOutput[], ruleMap: RuleMap) => {
  const items = outputs.map(output => {
    return {
      ...output,
      id: `${output.mappedKey}-${output.matchedDecisionRuleId}`
    };
  });

  const matchedDecisions = objectUtils.groupBy(items, 'matchedDecisionRuleId');
  return Object.entries(matchedDecisions).map(([key, values]: [string, any[]]) => {
    const table = (
      <div className="output-table" key={key}>
        <DataGrid
          idField="id"
          items={values}
          columnDefs={OUTPUT_COL_DEFS}
          selectable={false}
        />
      </div>
    );
    const rule = ruleMap.get(key);
    const matchedRules = displayMatchedRule(rule);

    return (
      <div key={key} className="output-table">
        <h5 className="io-heading">{key}</h5>
        {matchedRules}
        {table}
      </div>
    );
  });
};

type RuleMap = Map<string, CMxAPI.DecisionRule>;

const buildDecisionRuleMap = (decisionDTO: CMxAPI.DecisionDefinitionDto) => {
  const map: RuleMap = new Map<string, CMxAPI.DecisionRule>();
  const { decisionRules } = decisionDTO;
  decisionRules.forEach(rule => {
    map.set(rule.ruleId, rule);
  });
  return map;
};

const DMNTable: React.FC<DMNTableProps> = (props: DMNTableProps) => {
  const { step, ...spreadable } = props;

  const { decisions } = step;
  const [openState, setOpen] = useState({} as Record<string, boolean>);

  const handleToggleClick = (ev: React.MouseEvent) => {
    console.log(ev);
    ev.stopPropagation();
    //@ts-ignore
    const key = ev.target?.dataset.key ?? '';
    let newOpenState = { ...openState };
    newOpenState[key] = !openState[key];

    setOpen(newOpenState);
  };

  const items = decisions.map((decision: CMxAPI.Decision) => {
    // the decision key is not a unique enough id
    // we need to include executionTime as well.
    const key = `${decision.decisionDefinitionDto.decisionKey}-${decision.executionTime}`;

    const ruleMap = buildDecisionRuleMap(decision.decisionDefinitionDto);

    const hasState = {
      inputs: decision.decisionInputs.length > 0,
      outputs: decision.decisionOutputs.length > 0
    };
    const outputKey = `${key}-output`;
    const inputsKey = `${key}-inputs`;

    const classes = classnames({
      'has-io': hasState.inputs || hasState.outputs,
      'has-inputs': hasState.inputs,
      'has-outputs': hasState.outputs,
      'inputs-open': openState[inputsKey],
      'outputs-open': openState[outputKey]
    });

    const inputs = displayInputs(decision.decisionInputs);
    const outputs = displayOutputs(decision.decisionOutputs, ruleMap);

    return (
      <li key={decision.decisionDefinitionDto.decisionKey} className={classes}>
        {decision.decisionDefinitionDto.decisionName} <br />
        <div className="io-toggles">
          show:
          <label
            className="io-label outputs-label"
            htmlFor={outputKey}
            onClick={handleToggleClick}>
            <input id={outputKey} data-key={outputKey} type="checkbox" />{' '}
            <span className="inner-label">outputs</span>
          </label>
          <label
            className="io-label inputs-label"
            htmlFor={inputsKey}
            onClick={handleToggleClick}>
            <input id={inputsKey} data-key={inputsKey} type="checkbox" />{' '}
            <span className="inner-label">inputs</span>
          </label>
        </div>
        <div className="outputs-content">
          <h4 className="data-type">Outputs</h4>
          <ul>{outputs}</ul>
        </div>
        <div className="inputs-content">
          <h4 className="data-type">Inputs</h4>
          <ul>{inputs}</ul>
        </div>
      </li>
    );
  });

  const classes = classnames('dmn-table', {
    'no-items': items.length === 0
  });

  return (
    <div {...spreadable} className={classnames(classes)}>
      <ul>{items}</ul>
    </div>
  );
};
DMNTable.displayName = 'DMNTable';

export { DMNTable };
